import { useEffect, useState } from 'react';

import { styled } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import {
  Redirect, Route, Switch, useLocation
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SiteList from 'src/pages/SiteList';
import Dashboard from 'src/pages/Dashboard';
import FleetManagement from 'src/pages/FleetManagement';
import Tanks from 'src/pages/Tanks';
import Cards from 'src/pages/Cards';
import OtherReports from 'src/pages/OtherReports';
import Profile from 'src/pages/Profile';
import ScrollToTop from 'src/common/ScrollToTop';
import SuccessModal from 'src/common/SuccessModal';
import Vehicles from 'src/pages/Vehicles';
import Groups from 'src/pages/Groups';
import Operators from 'src/pages/Operators';
import DashboardNavbar, { SIDE_NAV_WIDTH, COLLAPSED_SIDE_NAV_WIDTH } from 'src/components/DashboardNavbar';
import DashboardSidebar from 'src/components/DashboardSidebar';
import SiteDetails from 'src/components/sites/SiteDetails';
import VehicleDetailsRoute from 'src/features/vehicle-details/VehicleDetailsRoute';
import VehicleEditRoute from 'src/features/vehicle-details/VehicleEditRoute';
import VehicleCreateRoute from 'src/features/vehicle-list/VehicleCreateRoute';
import OperatorDetails from 'src/components/operators/OperatorDetails';
import TankDetails from 'src/components/tanks/TankDetails';
import ROUTE from 'src/utils/routeConstants';
import Dispensers from 'src/pages/Dispensers';
import DispenserDetails from 'src/components/dispensers/DispenserDetails';
import CardDetails from 'src/components/cards/CardDetails';
import Drivers from 'src/pages/Drivers';
import DriverDetails from 'src/components/drivers/DriverDetails';
import Transactions from 'src/pages/Transactions';
import TransactionDetails from 'src/components/transactions/TransactionDetails';
import CardApprovals from 'src/pages/CardApprovals';
import ReconciliationReport from 'src/pages/ReconciliationReport';
import useAuth from 'src/context/AuthContext';
import { isAdminUser } from 'src/utils/getInitials';
import Requestors from 'src/pages/Requestors';
import RequestorDetails from 'src/components/requestors/RequestorDetails';
import SiteManagers from 'src/pages/SiteManager';
import SiteManagerDetails from 'src/components/siteManager/SiteManagerDetails';
import Approvers from 'src/pages/Approvers';
import ApproverDetails from 'src/components/approvers/ApproverDetails';
import { USER_ROLES } from 'src/utils/constants';
import ProtectedRoute from 'src/common/ProtectedRoute';
import VehicleBreakdown from 'src/pages/VehicleBreakdown';
// import SensorTransactions from 'src/pages/SensorTransactions';
import Settings from 'src/pages/Settings';
import VehicleStatusList from 'src/pages/VehicleStatusList';
import VehicleRecords from 'src/pages/VehicleRecords';
import Tyres from 'src/pages/Tyres';
import TyreDetails from 'src/components/tyres/TyreDetails';
import Notifications from 'src/pages/Notifications';
import { getUnreadNotificationsCount } from 'src/redux/notificationSlice';
import { isFleetOnlyModeEnabled } from 'src/utils/appMode';
import SupportPortal from 'src/pages/SupportPortal';
import TyreManagementV2 from 'src/features/tyre-management/TyreManagementV2';
import { isTyreManagementV2Enabled } from 'src/utils/tyreManagementFeature';
import PageHeaderContext from '../context/PageHeaderContext';

const MainLayoutRoot = styled('div', { shouldForwardProp: (prop) => prop !== 'sideNavWidth' })(({ theme, sideNavWidth }) => ({
  display: 'flex',
  flex: '1 1 auto',
  maxWidth: '100%',
  [theme.breakpoints.up('lg')]: {
    paddingLeft: sideNavWidth,
  }
}));

const MainLayoutContainer = styled('div')({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  width: '100%'
});

const getSharedPageHeader = (pathname) => {
  const headers = [
    ['/app/dashboard', ['DASHBOARD', 'Monitor fleet activity and fuel operations.']],
    ['/app/fleet-management', ['FLEET MANAGEMENT', 'Manage vehicles, tracking, and fleet operations.']],
    ['/app/sites', ['SITES', 'Manage fuel stations, assets, and operational status.']],
    ['/app/vehicles/cards', ['CARDS', 'Manage fuel cards, limits and configurations.']],
    ['/app/vehicles/groups', ['GROUPS', 'Manage fleet groups and departments.']],
    ['/app/vehicles/records', ['VEHICLE RECORDS', 'Review vehicle fuel and diesel records.']],
    ['/app/card-approvals', ['CARD APPROVALS', 'Review and manage fuel card requests.']],
    ['/app/card-requests', ['CARD REQUESTS', 'Manage your fuel card requests.']],
    ['/app/vehicle-breakdown', ['VEHICLE BREAKDOWN', 'Track and manage vehicle breakdowns.']],
    ['/app/reports/reconciliation', ['RECONCILIATION REPORT', 'Monitor daily fuel stock reconciliation across sites.']],
    ['/app/reports/others', ['OTHER REPORTS', 'View and download operational reports.']],
    ['/app/tanks', ['TANKS', 'Manage tanks and fuel inventory.']],
    ['/app/dispensers', ['DISPENSERS', 'Manage fuel dispensers and configurations.']],
    ['/app/users/approvers', ['APPROVERS', 'Manage transaction approval users.']],
    ['/app/users/operators', ['OPERATORS', 'Manage fleet operators and access.']],
    ['/app/users/requestors', ['REQUESTORS', 'Manage fuel requestors and access.']],
    ['/app/users/site-supervisor', ['SITE SUPERVISORS', 'Manage site supervisors and access.']],
    ['/app/users/vehicle-drivers', ['DRIVERS', 'Manage vehicle drivers.']],
    ['/app/notifications', ['NOTIFICATIONS', 'Review your latest system notifications.']],
    ['/app/account', ['ACCOUNT', 'Manage your account and company profile.']],
    ['/app/profile', ['PROFILE', 'Manage your profile details.']],
    ['/app/settings', ['SETTINGS', 'Manage application and company settings.']],
    ['/app/support', ['SUPPORT', 'Review and manage support requests.']]
  ];

  const match = headers.find(([route]) => pathname === route || pathname.startsWith(`${route}/`));
  return match ? { title: match[1][0], subtitle: match[1][1] } : null;
};

const DashboardLayout = () => {
  const location = useLocation();
  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(true);
  const [mobileCollapsed, setMobileCollapsed] = useState(false);
  const [pageHeader, setPageHeader] = useState(null);
  const dispatch = useDispatch();
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const isTransactionsPage = location.pathname === ROUTE.transactions;
  const isTransactionRoute = location.pathname.startsWith(ROUTE.transactions);
  const isTransactionDetailsPage = isTransactionRoute && !isTransactionsPage;
  const sideNavWidth = desktopCollapsed ? COLLAPSED_SIDE_NAV_WIDTH : SIDE_NAV_WIDTH;
  const { getUser } = useAuth();
  const user = getUser();
  const isAdmin = isAdminUser(getUser);
  const { appSettings, appSettingsLoaded } = useSelector((state) => state.settings);
  const isFleetOnlyMode = isFleetOnlyModeEnabled(appSettings);
  const isSupervisor = user?.role === USER_ROLES.siteManager;
  const isRequestor = user?.role === USER_ROLES.requestor;
  const isApprover = user?.role === USER_ROLES.approver;
  const isOperator = user?.role === USER_ROLES.operator;
  const isSupport = user?.role === USER_ROLES.support;
  const vehicleDetailsV2Active = Boolean(appSettings?.vehicle_details_v2_enabled);
  const tyreManagementV2Enabled = isTyreManagementV2Enabled(appSettings);
  const tyreV2KnownDisabled = appSettingsLoaded && !tyreManagementV2Enabled;
  const focusedTyreWorkspace = location.pathname === ROUTE.tyreV2Vehicles;
  const isVehicleOrTyrePage = location.pathname === '/app/vehicles'
    || location.pathname.startsWith('/app/vehicles/vehicle-list')
    || location.pathname.startsWith('/app/vehicles/records')
    || location.pathname.startsWith('/app/vehicle-status-list')
    || location.pathname.startsWith('/app/vehicles/tyre-management');
  const isLegacyVehicleListPage = appSettingsLoaded
    && !tyreManagementV2Enabled
    && location.pathname === ROUTE.vehicles;
  const isVehicleDetailsPage = location.pathname.startsWith('/app/vehicles/vehicle-list/');
  const isVehiclesHeaderPage = isLegacyVehicleListPage || isVehicleDetailsPage;
  const sharedPageHeader = isVehiclesHeaderPage
    ? { title: 'VEHICLES', subtitle: 'Manage vehicles, fuel usage, and assignments.' }
    : getSharedPageHeader(location.pathname);
  const isSharedVisualPage = !isTransactionRoute
    && (!isVehicleOrTyrePage || isLegacyVehicleListPage || isVehicleDetailsPage);
  const isV2VehicleOrTyrePage = isVehicleOrTyrePage && !isLegacyVehicleListPage;
  const canViewVehicleDetails = vehicleDetailsV2Active
    ? [
      USER_ROLES.admin,
      USER_ROLES.siteManager,
      USER_ROLES.supervisor,
      USER_ROLES.requestor,
      USER_ROLES.approver,
      USER_ROLES.operator,
    ].includes(user?.role)
    : isAdmin || isSupervisor;

  useEffect(() => {
    if (lgUp) {
      setMobileNavOpen(false);
      setMobileCollapsed(false);
    }
  }, [lgUp]);

  useEffect(() => {
    if (isOperator || isSupport) {
      return undefined;
    }
    dispatch(getUnreadNotificationsCount());

    const intervalId = setInterval(() => {
      dispatch(getUnreadNotificationsCount());
    }, 60000);

    return () => clearInterval(intervalId);
  }, [dispatch, isOperator, isSupport]);

  useEffect(() => {
    document.body.classList.toggle('transaction-ui-overlays', isSharedVisualPage);
    return () => document.body.classList.remove('transaction-ui-overlays');
  }, [isSharedVisualPage]);

  const transactionDetailHeader = isTransactionDetailsPage
    ? { title: 'TRANSACTIONS', subtitle: 'Manage and track fuel transactions.' }
    : null;
  const activePageHeader = isTransactionRoute
    ? (pageHeader || transactionDetailHeader)
    : sharedPageHeader;

  return (
    <PageHeaderContext.Provider value={{ pageHeader: activePageHeader, setPageHeader }}>
      {!focusedTyreWorkspace && (
        <DashboardNavbar
          onNavOpen={() => {
            if (isMobileNavOpen) {
              setMobileCollapsed((value) => !value);
              return;
            }

            setMobileCollapsed(false);
            setMobileNavOpen(true);
          }}
          onNavClose={() => setMobileNavOpen(false)}
          onToggleCollapse={() => setDesktopCollapsed(false)}
          isCollapsed={desktopCollapsed}
          sideNavWidth={sideNavWidth}
          mobileNavOpen={isMobileNavOpen}
          transactionPage={isTransactionsPage || isSharedVisualPage || isTransactionDetailsPage}
        />
      )}
      {!focusedTyreWorkspace && (
        <DashboardSidebar
          onMobileClose={() => setMobileNavOpen(false)}
          openMobile={isMobileNavOpen}
          isCollapsed={desktopCollapsed}
          mobileCollapsed={mobileCollapsed}
          width={sideNavWidth}
          onToggleCollapse={() => setDesktopCollapsed(false)}
          onToggleMobileCollapse={() => setMobileCollapsed(false)}
          onDesktopMouseEnter={() => setDesktopCollapsed(false)}
          onDesktopMouseLeave={() => setDesktopCollapsed(true)}
          onNavigate={() => {
            setDesktopCollapsed(true);
            setMobileNavOpen(false);
          }}
        />
      )}
      <SuccessModal />
      <MainLayoutRoot
        className={isTransactionRoute || isV2VehicleOrTyrePage ? undefined : 'transaction-ui-shell'}
        sideNavWidth={focusedTyreWorkspace ? 0 : sideNavWidth}
      >
        <MainLayoutContainer>
          <ScrollToTop />
          {isSupport && (
            <Switch>
              <Route
                exact
                path={ROUTE.app}
                render={() => <Redirect to={ROUTE.support} />}
              />
              <Route exact path={ROUTE.support} component={SupportPortal} />
              <Route render={() => <Redirect to={ROUTE.support} />} />
            </Switch>
          )}
          {!isSupport && isOperator && (
            <Switch>
              <Route
                exact
                path={ROUTE.app}
                render={() => <Redirect to={ROUTE.cardApprovals} />}
              />
              <Route exact path={ROUTE.cardApprovals} component={CardApprovals} />
              <Route render={() => <Redirect to={ROUTE.cardApprovals} />} />
            </Switch>
          )}
          {!isSupport && !isOperator && (
            <Switch>
              <Route
                exact
                path={ROUTE.app}
                render={() => <Redirect to="/app/dashboard" />}
              />
              <Route exact path={ROUTE.dashboard} component={Dashboard} />
              <Route exact path={ROUTE.notifications} component={Notifications} />
              {appSettings?.fleet_management === true && (
                <Route exact path={ROUTE.fleetManagement} component={FleetManagement} />
              )}
              <Route exact path={ROUTE.account} component={Profile} />
              <Route exact path={ROUTE.settings} component={Settings} />
              <Route exact path={ROUTE.vehicleStatusList} component={VehicleStatusList} />
              {isFleetOnlyMode
                ? <ProtectedRoute showRoute={isAdmin} exact path={ROUTE.sites} component={SiteList} />
                : <Route exact path={ROUTE.sites} component={SiteList} />}
              {isFleetOnlyMode
                ? <ProtectedRoute showRoute={isAdmin} exact path={ROUTE.siteDetails} component={SiteDetails} />
                : <Route exact path={ROUTE.siteDetails} component={SiteDetails} />}
              <Route exact path={ROUTE.profile} component={Profile} />
              {appSettings?.vehicle_management === true && (
                <Route exact path={ROUTE.vehicleBreakdown} component={VehicleBreakdown} />
              )}
              <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.vehicleManagement}>
                <Redirect to={ROUTE.vehicles} />
              </ProtectedRoute>
              {isFleetOnlyMode
                ? <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.vehicles} component={Vehicles} />
                : <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.vehicles} component={Vehicles} />}
              {isFleetOnlyMode
                ? <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.vehicleCreate} component={VehicleCreateRoute} />
                : <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.vehicleCreate} component={VehicleCreateRoute} />}
              {isFleetOnlyMode
                ? <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.vehicleEdit} component={VehicleEditRoute} />
                : <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.vehicleEdit} component={VehicleEditRoute} />}
              {isFleetOnlyMode
                ? <ProtectedRoute showRoute={canViewVehicleDetails} exact path={ROUTE.vehicleDetails} component={VehicleDetailsRoute} />
                : <ProtectedRoute showRoute={canViewVehicleDetails} exact path={ROUTE.vehicleDetails} component={VehicleDetailsRoute} />}
              {isFleetOnlyMode
                ? <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.groups} component={Groups} />
                : <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.groups} component={Groups} />}
              {isFleetOnlyMode
                ? <ProtectedRoute showRoute={isAdmin || isSupervisor || isApprover} exact path={ROUTE.transactions} component={Transactions} />
                : <Route exact path={ROUTE.transactions} component={Transactions} />}
              {isFleetOnlyMode
                ? <ProtectedRoute showRoute={isAdmin || isSupervisor || isApprover} exact path={ROUTE.transactionDetails} component={TransactionDetails} />
                : <Route exact path={ROUTE.transactionDetails} component={TransactionDetails} />}
              {!isFleetOnlyMode && <Route exact path={ROUTE.reconciliationReport} component={ReconciliationReport} />}
              {/* <Route exact path={ROUTE.wailonTransactions} component={SensorTransactions} /> */}
              {!isFleetOnlyMode && <Route exact path={ROUTE.otherReports} component={OtherReports} />}
              {!isFleetOnlyMode && <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.cards} component={Cards} />}
              {!isFleetOnlyMode && <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.cardDetails} component={CardDetails} />}
              {!isFleetOnlyMode && <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.vehicleRecords} component={VehicleRecords} />}
              {!isFleetOnlyMode && appSettings?.show_tyre_management && !tyreManagementV2Enabled && (
                <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.tyreManangement} component={Tyres} />
              )}
              {!isFleetOnlyMode && appSettings?.show_tyre_management && !tyreManagementV2Enabled && (
                <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.tyreDetails} component={TyreDetails} />
              )}
              {!isFleetOnlyMode && (tyreManagementV2Enabled || !appSettingsLoaded) && (
                <ProtectedRoute
                  showRoute={isAdmin || isSupervisor}
                  path={ROUTE.tyreManagementV2}
                  component={TyreManagementV2}
                />
              )}
              {!isFleetOnlyMode && tyreV2KnownDisabled && (
                <Route
                  path={ROUTE.tyreManagementV2}
                  render={() => <Redirect to={ROUTE.dashboard} />}
                />
              )}
              <ProtectedRoute
                showRoute={appSettings?.approve_request && (isApprover || isAdmin)}
                exact
                path={ROUTE.cardApprovals}
                component={CardApprovals}
              />
              <ProtectedRoute
                showRoute={appSettings?.approve_request && isRequestor}
                exact
                path={ROUTE.cardRequests}
                component={CardApprovals}
              />
              <ProtectedRoute showRoute={!isRequestor} exact path={ROUTE.tanks} component={Tanks} />
              <ProtectedRoute showRoute={!isRequestor} exact path={ROUTE.tankDetails} component={TankDetails} />
              <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.dispensers} component={Dispensers} />
              <ProtectedRoute showRoute={isAdmin || isSupervisor} exact path={ROUTE.dispenserDetails} component={DispenserDetails} />
              <ProtectedRoute showRoute={isAdmin} exact path={ROUTE.siteManagers} component={SiteManagers} />
              <ProtectedRoute showRoute={isAdmin} exact path={ROUTE.siteManagerDetails} component={SiteManagerDetails} />
              {!isFleetOnlyMode && appSettings?.approve_request && (
                <ProtectedRoute showRoute={isAdmin} exact path={ROUTE.approvers} component={Approvers} />
              )}
              {!isFleetOnlyMode && appSettings?.approve_request && (
                <ProtectedRoute showRoute={isAdmin} exact path={ROUTE.approverDetails} component={ApproverDetails} />
              )}
              {!isFleetOnlyMode && appSettings?.approve_request && (
                <ProtectedRoute showRoute={isAdmin} exact path={ROUTE.requestors} component={Requestors} />
              )}
              {!isFleetOnlyMode && appSettings?.approve_request && (
                <ProtectedRoute showRoute={isAdmin} exact path={ROUTE.requestorDetails} component={RequestorDetails} />
              )}
              {!isFleetOnlyMode && <ProtectedRoute showRoute={isAdmin} exact path={ROUTE.operators} component={Operators} />}
              {!isFleetOnlyMode && <ProtectedRoute showRoute={isAdmin} exact path={ROUTE.operatorDetails} component={OperatorDetails} />}
              {!isFleetOnlyMode && <ProtectedRoute showRoute={isAdmin} exact path={ROUTE.drivers} component={Drivers} />}
              {!isFleetOnlyMode && <ProtectedRoute showRoute={isAdmin} exact path={ROUTE.driverDetails} component={DriverDetails} />}
            </Switch>
          )}
        </MainLayoutContainer>
      </MainLayoutRoot>
    </PageHeaderContext.Provider>
  );
};

export default DashboardLayout;
